import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import UploadImageDialog from '../components/UploadImageDialog';
import { uploadImage } from '../utils/uploadImage';

const api = () => process.env.REACT_APP_BACKEND_ADDRESS;
const authHeaders = () => ({ 'Authorization': `Bearer ${Cookies.get('token')}`, 'Content-Type': 'application/json' });

const Profile = () => {
    const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user')) || {});

    useEffect(() => {
        const onStorage = () => setUser(JSON.parse(localStorage.getItem('user')) || {});
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // Edit profile modal
    const [snackbar, setSnackbar] = useState(null);

    const [editOpen, setEditOpen] = useState(false);
    const [profile, setProfile] = useState({});
    const [profileStatus, setProfileStatus] = useState(null);
    const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);

    const openEdit = () => {
        setProfile({
            first_name:   user.first_name   ?? '',
            last_name:    user.last_name    ?? '',
            username:     user.username     ?? '',
            email:        user.email        ?? '',
            avatar_url:   user.avatar_url   ?? '',
            display_name: user.display_name ?? '',
            bio:          user.bio          ?? '',
        });
        setProfileStatus(null);
        setEditOpen(true);
    };

    const handleProfileSave = async () => {
        setProfileStatus(null);
        try {
            const res = await fetch(`${api()}/users/${user.id}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({
                    first_name:   profile.first_name,
                    last_name:    profile.last_name,
                    username:     profile.username,
                    email:        profile.email,
                    avatar_url:   profile.avatar_url || null,
                    display_name: profile.display_name || null,
                    bio:          profile.bio || null,
                }),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail ?? 'Failed to update profile');
            }
            const updated = await res.json();
            const newUser = { ...user, ...updated };
            localStorage.setItem('user', JSON.stringify(newUser));
            window.dispatchEvent(new Event('storage'));
            setEditOpen(false);
            setSnackbar('Profile updated successfully');
        } catch (e) {
            setProfileStatus({ type: 'error', message: e.message });
        }
    };

    const handleAvatarUpload = (file) => {
        setProfileStatus({ type: 'info', message: 'Uploading photo…' });
        uploadImage(
            file,
            (err) => {
                console.error('Avatar upload error:', err);
                setProfileStatus({ type: 'error', message: 'Failed to upload photo' });
            },
            async ([, downloadURL]) => {
                setProfile(p => ({ ...p, avatar_url: downloadURL }));
                try {
                    const res = await fetch(`${api()}/users/${user.id}`, {
                        method: 'PUT',
                        headers: authHeaders(),
                        body: JSON.stringify({ avatar_url: downloadURL }),
                    });
                    if (!res.ok) throw new Error();
                    const updated = await res.json();
                    const newUser = { ...JSON.parse(localStorage.getItem('user') || '{}'), ...updated };
                    localStorage.setItem('user', JSON.stringify(newUser));
                    window.dispatchEvent(new Event('storage'));
                    setProfileStatus({ type: 'success', message: 'Photo updated' });
                } catch {
                    setProfileStatus({ type: 'error', message: 'Photo uploaded but failed to save' });
                }
            },
        );
    };

    // Change password modal
    const [pwOpen, setPwOpen] = useState(false);
    const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
    const [passwordStatus, setPasswordStatus] = useState(null);

    const openPassword = () => {
        setPasswords({ current_password: '', new_password: '', confirm_password: '' });
        setPasswordStatus(null);
        setPwOpen(true);
    };

    const handlePasswordChange = async () => {
        setPasswordStatus(null);
        if (passwords.new_password !== passwords.confirm_password) {
            setPasswordStatus({ type: 'error', message: 'New passwords do not match' });
            return;
        }
        if (passwords.new_password.length < 8) {
            setPasswordStatus({ type: 'error', message: 'Password must be at least 8 characters' });
            return;
        }
        try {
            const res = await fetch(`${api()}/users/${user.id}/password`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({
                    current_password: passwords.current_password,
                    new_password: passwords.new_password,
                }),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail ?? 'Failed to change password');
            }
            setPasswordStatus({ type: 'success', message: 'Password changed' });
            setPasswords({ current_password: '', new_password: '', confirm_password: '' });
        } catch (e) {
            setPasswordStatus({ type: 'error', message: e.message });
        }
    };

    const initials = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();
    const displayName = user.display_name || `${user.first_name} ${user.last_name}`;

    return (
        <Container maxWidth="sm" sx={{ mt: 6, pb: 12 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Avatar
                    src={user.avatar_url || undefined}
                    sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: '2rem', mb: 1 }}
                >
                    {initials}
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{displayName}</Typography>
                {user.display_name && (
                    <Typography variant="body2" color="text.secondary">
                        {user.first_name} {user.last_name}
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary">@{user.username}</Typography>
                {user.bio && (
                    <Typography variant="body1" sx={{ mt: 1, textAlign: 'center', maxWidth: 400 }}>
                        {user.bio}
                    </Typography>
                )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Info grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {[
                    { label: 'First Name', value: user.first_name },
                    { label: 'Last Name',  value: user.last_name },
                    { label: 'Username',   value: user.username ? `@${user.username}` : '—' },
                    { label: 'Email',      value: user.email },
                ].map(({ label, value }) => (
                    <Box key={label}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>{value ?? '—'}</Typography>
                    </Box>
                ))}
            </Box>

            {/* Actions */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" startIcon={<EditIcon />} onClick={openEdit}>
                    Edit Profile
                </Button>
                <Button variant="outlined" startIcon={<LockIcon />} onClick={openPassword}>
                    Change Password
                </Button>
                <Button variant="outlined" component={Link} to={`/profile/${user.id}`}>
                    View My Page
                </Button>
            </Box>

            {/* Edit Profile modal */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><EditIcon sx={{ color: 'primary.main' }} />Edit Profile</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            src={profile.avatar_url || undefined}
                            sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: '1.25rem' }}
                        >
                            {initials}
                        </Avatar>
                        <Button variant="outlined" size="small" onClick={() => setAvatarDialogOpen(true)}>
                            Change Photo
                        </Button>
                        {profile.avatar_url && (
                            <Button size="small" color="error" onClick={() => setProfile(p => ({ ...p, avatar_url: '' }))}>
                                Remove
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="First Name" value={profile.first_name ?? ''} fullWidth size="small"
                            onChange={(e) => setProfile(p => ({ ...p, first_name: e.target.value }))}
                        />
                        <TextField
                            label="Last Name" value={profile.last_name ?? ''} fullWidth size="small"
                            onChange={(e) => setProfile(p => ({ ...p, last_name: e.target.value }))}
                        />
                    </Box>
                    <TextField
                        label="Username" value={profile.username ?? ''} fullWidth size="small"
                        onChange={(e) => setProfile(p => ({ ...p, username: e.target.value }))}
                    />
                    <TextField
                        label="Display Name" value={profile.display_name ?? ''} fullWidth size="small"
                        placeholder={`${profile.first_name} ${profile.last_name}`.trim()}
                        helperText="How your name appears on recipes. Defaults to first and last name."
                        onChange={(e) => setProfile(p => ({ ...p, display_name: e.target.value }))}
                    />
                    <TextField
                        label="Email" type="email" value={profile.email ?? ''} fullWidth size="small"
                        onChange={(e) => setProfile(p => ({ ...p, email: e.target.value }))}
                    />
                    <TextField
                        label="About Me" value={profile.bio ?? ''} fullWidth size="small"
                        multiline rows={3}
                        placeholder="A little about yourself, your cooking style, family recipes you're keeping alive…"
                        onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))}
                    />
                    {profileStatus && (
                        <Alert severity={profileStatus.type} onClose={() => setProfileStatus(null)}>
                            {profileStatus.message}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleProfileSave}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* Change Password modal */}
            <Dialog open={pwOpen} onClose={() => setPwOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><LockIcon sx={{ color: 'primary.main' }} />Change Password</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        label="Current Password" type="password" value={passwords.current_password} fullWidth size="small"
                        onChange={(e) => setPasswords(p => ({ ...p, current_password: e.target.value }))}
                    />
                    <TextField
                        label="New Password" type="password" value={passwords.new_password} fullWidth size="small"
                        onChange={(e) => setPasswords(p => ({ ...p, new_password: e.target.value }))}
                    />
                    <TextField
                        label="Confirm New Password" type="password" value={passwords.confirm_password} fullWidth size="small"
                        onChange={(e) => setPasswords(p => ({ ...p, confirm_password: e.target.value }))}
                    />
                    {passwordStatus && (
                        <Alert severity={passwordStatus.type} onClose={() => setPasswordStatus(null)}>
                            {passwordStatus.message}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPwOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handlePasswordChange}>Change Password</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(snackbar)}
                autoHideDuration={3000}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
                    {snackbar}
                </Alert>
            </Snackbar>

            <UploadImageDialog
                open={avatarDialogOpen}
                onClose={() => setAvatarDialogOpen(false)}
                onUpload={handleAvatarUpload}
            />
        </Container>
    );
};

export default Profile;
